/**
 * MongoDB Bounded Context Pattern Repository
 * 
 * Infrastructure adapter implementing repository pattern for BoundedContextPattern aggregate.
 * Provides persistence capabilities using MongoDB with proper error handling.
 */

import { Collection, getMongoDb } from "@swoft/persistence";
import { BoundedContextPattern, BoundedContextPatternData } from '../../domain/entities/BoundedContextPattern';
import type { IBoundedContextPatternRepository } from '../../application/services/BoundedContextAnalysisService';

export class MongoBoundedContextPatternRepository implements IBoundedContextPatternRepository {
  private collection?: Collection<BoundedContextPatternData>;

  private getCollection(): Collection<BoundedContextPatternData> {
    if (!this.collection) {
      const db = getMongoDb();
      this.collection = db.collection<BoundedContextPatternData>('boundedContextPatterns');
    }
    return this.collection;
  }

  async save(pattern: BoundedContextPattern): Promise<void> {
    try {
      const data = pattern.toData();
      
      const collection = this.getCollection();
      await collection.replaceOne(
        { id: data.id },
        data,
        { upsert: true }
      );
    } catch (error) {
      throw new Error(`Failed to save bounded context pattern: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findById(id: string): Promise<BoundedContextPattern | null> {
    try {
      const data = await this.collection.findOne({ id });
      
      return data ? BoundedContextPattern.fromData(data) : null;
    } catch (error) {
      throw new Error(`Failed to find bounded context pattern by id: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findByPackageName(packageName: string): Promise<BoundedContextPattern | null> {
    try {
      const data = await this.collection.findOne({ packageName });
      
      return data ? BoundedContextPattern.fromData(data) : null;
    } catch (error) {
      throw new Error(`Failed to find bounded context pattern by package name: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findAll(): Promise<BoundedContextPattern[]> {
    try {
      const cursor = this.collection.find({});
      const data = await cursor.toArray();
      
      return data.map(d => BoundedContextPattern.fromData(d));
    } catch (error) {
      throw new Error(`Failed to find all bounded context patterns: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findByType(contextType: 'core' | 'supporting' | 'generic'): Promise<BoundedContextPattern[]> {
    try {
      const cursor = this.collection.find({ contextType });
      const data = await cursor.toArray();
      
      return data.map(d => BoundedContextPattern.fromData(d));
    } catch (error) {
      throw new Error(`Failed to find bounded context patterns by type: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}